import React, { useState } from 'react'
import { FormDataConsumer, required, SelectInput } from 'react-admin'
import { BaseProductPackage } from 'services/api/entities/catalog/packages/common'
import { FoodPackage } from 'services/api/entities/catalog/packages/foodPackage'
import { OptionedPackage } from 'services/api/entities/catalog/packages/common'
import { useKGetList } from 'services/hooks/useKGetList'
import { activityStatusChoices } from 'services/cfg/form'
import SingleImageInput from 'ui-shared/WrappedInputs/SingleImageInput'
import { DrinkPackage } from 'services/api/entities/catalog/packages/drinkPackage'
import {
  getMessageIfPhysicalIsNotIdealForLocation,
  hasPackagesWithCompatibleMinAndMaxQuantity,
} from 'services/utils/validation'
import { LocationPackage } from 'services/api/entities/catalog/packages/locationPackage'
import {
  getLocationPackageWithMinAndMaxQuantity,
  getPackageWithMinAndMaxQuantity,
  PackagesWithMinAndMaxQuantityMap,
} from './common'
import TailoredInput from 'ui-shared/Tailored/TailoredInput'
import ProductForm from 'ui-shared/Forms/ProductForm'
import { FormDataConsumerProps } from 'types/common'
import { EventPackage } from 'services/api/entities/catalog/packages/eventPackage'
import { OptionedProductPresetFields } from '../../OptionedProductPresetFields'
import DrinkPresetAdditionalFields from '../../drinkPackage/DrinkPresetAdditionalFields'
import { FoodPresetAdditionalFields } from '../../foodPackage/FoodPresetAdditionalFields'
import PhysicalPackageReadonlyPresetsList from '../../physicalPackage/PhysicalPackageReadonlyPresetsList'
import LocationPackageReadonlyPresetsList from '../../locationPackage/LocationPackageReadonlyPresetsList'
import FoodPackageReadonlyPresetsList from '../../foodPackage/FoodPackageReadonlyPresetsList'
import EventPackageFormPackageAccordion from '../EventPackageFormPackageAccordion'
import DrinkPackageReadonlyPresetsList from '../../drinkPackage/DrinkPackageReadonlyPresetsList'
import BaseProductPresetReadonlyList from '../../BaseProductPresetReadonlyList'
import { styled } from '@mui/system'
import RatioMultiplier from '../../RatioMultiplier'
import LocationPresetAdditionalFields from '../../locationPackage/LocationAdditionalFields'
import { yupResolver } from '@hookform/resolvers/yup'
import { eventPackageVS } from 'services/validations/catalog/packages/eventPackage'
import { BaseProduct } from 'services/api/entities/catalog/baseProduct/types/internal/baseProduct-models'
import { Location } from 'services/api/entities/catalog/location/types/internal/location-models'
import { Drink } from 'services/api/entities/catalog/drink/types/internal/drink-models'
import { Food } from 'services/api/entities/catalog/food/types/internal/food-models'
import { Experience } from 'services/api/entities/catalog/experience/types/internal/experience-models'
import { ProfessionalFigure } from 'services/api/entities/catalog/professional/types/internal/professional-models'
const PREFIX = 'EventPackageForm'

const classes = {
  redText: `${PREFIX}-redText`,
}

const StyledProductForm = styled(ProductForm)({
  [`& .${classes.redText}`]: {
    color: '#FF0000',
  },
})

const EventPackageForm: React.FC = () => {
  const locations = useKGetList<Location>('locations')
  const physicals = useKGetList<Experience>('physicals')
  const foods = useKGetList<Food>('foods')
  const professionals = useKGetList<ProfessionalFigure>('professionals')
  const rents = useKGetList<BaseProduct>('rents')
  const musics = useKGetList<BaseProduct>('musics')
  const drinks = useKGetList<Drink>('drinks')
  const [customers, setCustomers] = useState<number[]>([])
  const [organizations, setOrganizations] = useState<number[]>([])
  const setFilters = (customers: number[], organizations: number[]) => {
    setCustomers(customers)
    setOrganizations(organizations)
  }
  const packagesMap: PackagesWithMinAndMaxQuantityMap = {
    locationPackage: {
      packages: useKGetList<LocationPackage>('locationpackages').map(
        getLocationPackageWithMinAndMaxQuantity(locations)
      ),
      products: locations,
    },
    physicalPackage: {
      packages: useKGetList<OptionedPackage>('physicalpackages').map(
        getPackageWithMinAndMaxQuantity(physicals)
      ),
      products: physicals,
    },
    foodPackage: {
      packages: useKGetList<FoodPackage>('foodpackages').map(
        getPackageWithMinAndMaxQuantity(foods)
      ),
      products: foods,
    },
    professionalPackage: {
      packages: useKGetList<BaseProductPackage>('professionalpackages').map(
        getPackageWithMinAndMaxQuantity(professionals)
      ),
      products: professionals,
    },
    rentPackage: {
      packages: useKGetList<BaseProductPackage>('rentpackages').map(
        getPackageWithMinAndMaxQuantity(rents)
      ),
      products: rents,
    },
    musicPackage: {
      packages: useKGetList<BaseProductPackage>('musicpackages').map(
        getPackageWithMinAndMaxQuantity(musics)
      ),
      products: musics,
    },
    drinkPackage: {
      packages: useKGetList<DrinkPackage>('drinkpackages').map(
        getPackageWithMinAndMaxQuantity(drinks)
      ),
      products: drinks,
    },
  }

  return (
    <StyledProductForm resolver={yupResolver(eventPackageVS)}>
      <SelectInput
        label="form.fields.status"
        source="status"
        choices={activityStatusChoices}
        required
      />
      <SingleImageInput validate={required()} />
      <TailoredInput
        validationMessage="form.fields.packageInSectionValidation"
        setFilter={setFilters}
      />
      <FormDataConsumer>
        {({ formData }: FormDataConsumerProps<EventPackage>) => {
          return (
            <>
              <EventPackageFormPackageAccordion
                choices={locations}
                eventPkgCustomers={customers}
                eventPkgOrgs={organizations}
                packages={packagesMap.locationPackage.packages}
                formDataPackage={formData.locationPackage}
                source="locationPackage"
                title="Location Packages"
                presetsReadOnlyComponent={
                  <LocationPackageReadonlyPresetsList />
                }
                presetInputFields={<LocationPresetAdditionalFields />}
              />

              <EventPackageFormPackageAccordion
                packages={packagesMap.physicalPackage.packages}
                formDataPackage={formData.physicalPackage}
                eventPkgCustomers={customers}
                eventPkgOrgs={organizations}
                choices={physicals}
                source="physicalPackage"
                title="Physical Packages"
                presetsReadOnlyComponent={
                  <PhysicalPackageReadonlyPresetsList />
                }
                presetInputFields={<OptionedProductPresetFields />}
              />
              <EventPackageFormPackageAccordion
                formDataPackage={formData.foodPackage}
                packages={packagesMap.foodPackage.packages}
                eventPkgCustomers={customers}
                eventPkgOrgs={organizations}
                choices={foods}
                source="foodPackage"
                title="Food Packages"
                presetsReadOnlyComponent={<FoodPackageReadonlyPresetsList />}
                presetInputFields={<FoodPresetAdditionalFields />}
              />

              <EventPackageFormPackageAccordion
                packages={packagesMap.drinkPackage.packages}
                formDataPackage={formData.drinkPackage}
                eventPkgCustomers={customers}
                eventPkgOrgs={organizations}
                choices={drinks}
                source="drinkPackage"
                title="Drink Packages"
                presetsReadOnlyComponent={<DrinkPackageReadonlyPresetsList />}
                isToggleBundleVisible
                presetInputFields={<DrinkPresetAdditionalFields />}
              />

              <EventPackageFormPackageAccordion
                formDataPackage={formData.professionalPackage}
                packages={packagesMap.professionalPackage.packages}
                eventPkgCustomers={customers}
                eventPkgOrgs={organizations}
                choices={professionals}
                source="professionalPackage"
                title="Professional Packages"
                presetsReadOnlyComponent={<BaseProductPresetReadonlyList />}
                presetInputFields={<RatioMultiplier />}
              />

              <EventPackageFormPackageAccordion
                formDataPackage={formData.musicPackage}
                packages={packagesMap.musicPackage.packages}
                eventPkgCustomers={customers}
                eventPkgOrgs={organizations}
                choices={musics}
                source="musicPackage"
                title="Music Packages"
                presetsReadOnlyComponent={<BaseProductPresetReadonlyList />}
                presetInputFields={<RatioMultiplier />}
              />

              <EventPackageFormPackageAccordion
                formDataPackage={formData.rentPackage}
                packages={packagesMap.rentPackage.packages}
                eventPkgCustomers={customers}
                eventPkgOrgs={organizations}
                choices={rents}
                source="rentPackage"
                title="Rent Packages"
                presetsReadOnlyComponent={<BaseProductPresetReadonlyList />}
                presetInputFields={<RatioMultiplier />}
              />
            </>
          )
        }}
      </FormDataConsumer>
      <FormDataConsumer>
        {({ formData }: FormDataConsumerProps<EventPackage>) => {
          const errors = {
            physicalNotIdealError:
              getMessageIfPhysicalIsNotIdealForLocation(packagesMap)(formData),
            minMaxError:
              hasPackagesWithCompatibleMinAndMaxQuantity(packagesMap)(formData),
          }

          return Object.entries(errors).map(([key, value]) => {
            return (
              value && (
                <p key={key} className={classes.redText}>
                  &bull; {value}
                </p>
              )
            )
          })
        }}
      </FormDataConsumer>
    </StyledProductForm>
  )
}

export default EventPackageForm
